import { createFileRoute, redirect } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { useMemo, type JSX } from "react";

import { useFragment } from "@repo/graphql-types";
import { graphql } from "@repo/graphql-types/gql";

import { AppointmentPatientListFragment } from "@/components/appointment/patient/appointment-patient-list";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import {
  RecurringPatientList,
  RecurringPatientListFragment,
} from "@/components/recurring-appointment/recurring-patient-list";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useGraphQL } from "@/hooks/use-graphql";
import { recurringSlotAtom } from "@/lib/atoms/recurring-appointment-form-atom";

const RecurringSearchPatientPageQuery = graphql(/* GraphQL */ `
  query RecurringSearchPatientPageQuery {
    ...RecurringPatientListFragment
  }
`);

const RecurringPatientLimitsQuery = graphql(/* GraphQL */ `
  query RecurringPatientLimitsQuery($codPacientes: [Int!]!) {
    limitesRecorrenciaPorPaciente(codPacientes: $codPacientes) {
      codPaciente
      limite
      disponivel
      atingiuMaximoDeAgendamentosPendentes
    }
  }
`);

const RecurringAppointmentSearchPatientPage = (): JSX.Element => {
  const queryResult = useGraphQL(RecurringSearchPatientPageQuery, {});
  const { data } = queryResult;

  const listData = useFragment(RecurringPatientListFragment, data);
  const patientsData = useFragment(AppointmentPatientListFragment, listData);

  const codPacientes = useMemo(
    () =>
      patientsData?.pacientes.map((currentPatient) => currentPatient.codPaciente) ?? [],
    [patientsData],
  );

  const limitsResult = useGraphQL(
    RecurringPatientLimitsQuery,
    { codPacientes },
    { enabled: codPacientes.length > 0 },
  );

  const patientLimits = useMemo(
    () =>
      (limitsResult.data?.limitesRecorrenciaPorPaciente ?? []).flatMap((limit) =>
        limit
          ? [
              {
                codPaciente: limit.codPaciente,
                limite: limit.limite,
                disponivel: limit.disponivel,
                atingiuMaximoDeAgendamentosPendentes:
                  limit.atingiuMaximoDeAgendamentosPendentes,
              },
            ]
          : [],
      ),
    [limitsResult.data],
  );

  return (
    <RoundedLayout>
      <RecurrenceCreateHeader title="Selecione o paciente" />
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <RecurringPatientList data={data} patientLimits={patientLimits} />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/create/search-patient")({
  component: RecurringAppointmentSearchPatientPage,
  beforeLoad: () => {
    const slot = getDefaultStore().get(recurringSlotAtom);

    if (!slot.horaInicio) {
      redirect({ to: "/recurring-appointments/create", throw: true });
    }
  },
});
